import { SUPABASE_URL } from '@/integrations/supabase/client';

/**
 * Utility to update web app manifest to use brand's icon
 * This affects the left icon in push notifications (app icon from manifest)
 */

export const updateManifestIcons = async (brandId: string | null, supabase: any) => {
  if (!brandId || !supabase || typeof document === 'undefined') return;

  try {
    // Get the brand's icon (push notification icon or favicon)
    let iconUrl: string | null = null;

    // First, try to get the push notification icon
    const { data: pushSettings } = await supabase
      .from('brand_push_notification_settings')
      .select('icon_asset_id')
      .eq('brand_id', brandId)
      .maybeSingle();

    if (pushSettings?.icon_asset_id) {
      const { data: asset } = await supabase
        .from('brand_assets')
        .select('file_url')
        .eq('id', pushSettings.icon_asset_id)
        .single();

      if (asset?.file_url) {
        iconUrl = asset.file_url;
      }
    }

    // Fall back to brand favicon if no push notification icon
    if (!iconUrl) {
      const { data: brand } = await supabase
        .from('brands')
        .select('favicon_asset_id')
        .eq('id', brandId)
        .single();

      if (brand?.favicon_asset_id) {
        const { data: asset } = await supabase
          .from('brand_assets')
          .select('file_url')
          .eq('id', brand.favicon_asset_id)
          .single();

        if (asset?.file_url) {
          iconUrl = asset.file_url;
        }
      }
    }

    if (!iconUrl) return;

    // Update the manifest link to point to dynamic endpoint with brand_id
    const manifestLink = document.querySelector('link[rel="manifest"]') as HTMLLinkElement;
    if (manifestLink) {
      const manifestUrl = `${SUPABASE_URL}/functions/v1/site-webmanifest?brand_id=${brandId}`;
      manifestLink.href = manifestUrl;
      // Manifest link updated silently
    }

    // Also update icon links in HTML (for browsers that read these directly)
    updateIconLinks(iconUrl);
  } catch (error) {
    console.error('Error updating manifest icons:', error);
  }
};

const updateIconLinks = (iconUrl: string) => {
  if (!iconUrl || typeof document === 'undefined') return;

  try {
    // Update apple-touch-icon (used by iOS)
    let appleTouchIcon = document.querySelector('link[rel="apple-touch-icon"]') as HTMLLinkElement;
    if (!appleTouchIcon) {
      appleTouchIcon = document.createElement('link');
      appleTouchIcon.rel = 'apple-touch-icon';
      appleTouchIcon.setAttribute('sizes', '180x180');
      document.head.appendChild(appleTouchIcon);
    }
    appleTouchIcon.href = iconUrl;

    // Update shortcut icon
    let shortcutIcon = document.querySelector('link[rel="shortcut icon"]') as HTMLLinkElement;
    if (shortcutIcon) {
      shortcutIcon.href = iconUrl;
    }

    // Update all icon links
    const allIcons = document.querySelectorAll('link[rel="icon"]');
    allIcons.forEach((icon) => {
      const link = icon as HTMLLinkElement;
      link.href = iconUrl;
    });

    // Icon links updated silently
  } catch (error) {
    console.error('Error updating icon links:', error);
  }
};

